import React, { useEffect, useMemo, useState } from "react";
import MainNavigation from "../../../components/navigation/MainNavigation";
import ImpactRadarGraph from "../../../components/graphs/ImpactRadarGraph";
// import { data } from "./MockData";
import { useAppDispatch } from "../../../store";
import { fetchImpact } from "../../../store/slices/impact";
import { useSelector } from "react-redux";
import "./impactRadar.scss";
import WeekPicker from "../../../components/ui/weekPicker/WeekPicker";
import { DateTime } from "luxon";
import MobileNavigation from "../../../components/navigation/MobileNavigation";
import { useSearchParams } from "react-router-dom";

const CHANNELS = {
  TOTAL: "total",
  FACEBOOK: "facebook",
  LINKEDIN: "linkedin",
  MOBILE: "mobile",
  NEWSLETTER: "newsletter",
  TWITTER: "twitter",
  YOUTUBE: "youtube",
  VIDEO: "video",
};

const ImpactRadar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryChannel = searchParams.get("channel") || null;
  const queryFrom = searchParams.get("from") || null;
  const queryTo = searchParams.get("to") || null;

  const [week, setWeek] = useState({ firstDay: queryFrom || "", lastDay: queryTo || "" });
  const [selectedChannel, setSelectedChannel] = useState(queryChannel || CHANNELS.TOTAL);
  const currentBrand = useSelector((state: any) => state.currentBrand.data);
  const dispatch = useAppDispatch();
  const data = useSelector((state: any) => state.impact?.data.graphdata) || [];
  const dataLoading = useSelector((state: any) => state.impact?.isLoading);

  useEffect(() => {
    document.title = "Impact Radar";
    document.body.dataset.titleReady = "true";
  }, []);

  const hourTohourScale = (hour, startHour) => {
    let startHourD = startHour || 6;
    return ((hour + (24 - startHour)) % 24) + startHourD;
  };

  useEffect(() => {
    if (!week) return;
    if (week.firstDay === "" || week.lastDay === "") return;
    const dateObjectFrom = new Date(week?.firstDay);
    let dateObjectTo = new Date(week?.lastDay);
    dateObjectTo.setDate(dateObjectTo.getDate() + 1);

    let dateObjectToParam = new Date(week?.lastDay);

    const yearFrom = dateObjectFrom.getFullYear();
    const monthFrom = String(dateObjectFrom.getMonth() + 1).padStart(2, "0");
    const dayFrom = String(dateObjectFrom.getDate()).padStart(2, "0");

    const yearTo = dateObjectTo.getFullYear();
    const monthTo = String(dateObjectTo.getMonth() + 1).padStart(2, "0");
    const dayTo = String(dateObjectTo.getDate()).padStart(2, "0");

    const yearToParam = dateObjectToParam.getFullYear();
    const monthToParam = String(dateObjectToParam.getMonth() + 1).padStart(2, "0");
    const dayToParam = String(dateObjectToParam.getDate()).padStart(2, "0");

    const formattedDateFrom = `${yearFrom}-${monthFrom}-${dayFrom}`;
    const formattedDateTo = `${yearTo}-${monthTo}-${dayTo}`;
    const formattedDateToParam = `${yearToParam}-${monthToParam}-${dayToParam}`;
    setSearchParams({
      channel: selectedChannel,
      from: formattedDateFrom,
      to: formattedDateToParam,
    });

    const channelFilter = selectedChannel === CHANNELS.VIDEO ? "videos" : selectedChannel;
    dispatch(fetchImpact(formattedDateFrom, formattedDateTo, channelFilter));
  }, [week, dispatch, selectedChannel, setSearchParams]);

  const impactData = useMemo(() => {
    return { graphdata: data };
  }, [data]);

  const transformedEngagementData = useMemo(() => {
    if (impactData.graphdata.length === 0) return [];
    const engagementData = impactData?.graphdata[1]?.data;
    let transformedData = [] as Array<any>;

    for (const timestamp in engagementData) {
      if (engagementData.hasOwnProperty(timestamp)) {
        const dt = DateTime.fromMillis(parseInt(timestamp)).setZone(currentBrand.domainSettings.timezone);
        const day = dt.weekday - 1; // Sunday is 0, so subtract 1 for consistency with Date.getDay()
        const hour = dt.hour;
        const value = engagementData[timestamp];
        const dateD = dt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

        let dateObjectTo = new Date(week?.lastDay);
        dateObjectTo.setDate(dateObjectTo.getDate() + 1);
        dateObjectTo.setHours(6, 0, 0, 0);

        if (dt < dateObjectTo) {
          transformedData.push({
            date: dt,
            day: day,
            hour: hourTohourScale(hour, 6),
            value: value,
            timestamp: timestamp,
            dateD: dateD,
          });
        }
      }
    }
    return transformedData;
  }, [impactData, currentBrand.domainSettings.timezone, week?.lastDay]);

  const transformedImpactData = useMemo(() => {
    if (impactData.graphdata.length === 0) return [];
    const impactDataOnly = impactData?.graphdata[2]?.data;
    let transformedData = [] as Array<any>;

    for (const timestamp in impactDataOnly) {
      if (impactDataOnly.hasOwnProperty(timestamp)) {
        const dt = DateTime.fromMillis(parseInt(timestamp)).setZone(currentBrand.domainSettings.timezone);
        const day = dt.weekday - 1; // Sunday is 0, so subtract 1 for consistency with Date.getDay()
        const hour = dt.hour;
        const value = impactDataOnly[timestamp];
        const dateD = dt.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

        let dateObjectTo = new Date(week?.lastDay);
        dateObjectTo.setDate(dateObjectTo.getDate() + 1);
        dateObjectTo.setHours(6, 0, 0, 0);

        if (dt < dateObjectTo) {
          transformedData.push({
            date: dt,
            day: day,
            hour: hourTohourScale(hour, 6),
            value: value,
            timestamp: timestamp,
            dateD: dateD,
          });
        }
      }
    }
    return transformedData;
  }, [impactData, currentBrand.domainSettings.timezone, week?.lastDay]);

  const channelsArray = Object.values(CHANNELS);

  const handleWeekChange = (week) => {
    setWeek(week);
  };

  const startDate = useMemo(() => {
    if (impactData.graphdata.length === 0) return "";
    return new Date(parseInt(impactData.graphdata[0]?.startdate, 10));
  }, [impactData, week.firstDay]);

  const endDate = useMemo(() => {
    if (impactData.graphdata.length === 0) return "";
    return new Date(parseInt(impactData.graphdata[0]?.enddate, 10));
  }, [impactData]);

  const changeChannel = (channel) => {
    setSelectedChannel(channel);
  };

  return (
    <div className="impact-radar-page-wrapper">
      <MainNavigation />
      <MobileNavigation title="Impact Radar" />
      <div className="impact-radar-page">
        <h1>Impact Radar</h1>
        <div className="impact-radar-options">
          <div className="week-picker-container">
            <p className="week-picker-title">Select week: </p>
            <WeekPicker onChange={handleWeekChange} initialValues={week} />
          </div>
          <div className="channels-wrapper">
            <p className="channels-tittle">Channels</p>
            <div className="channels-container">
              {channelsArray.map((channel) => {
                return (
                  <div
                    key={channel}
                    className={`channel-name ${channel === selectedChannel ? "active" : ""}`}
                    onClick={() => {
                      changeChannel(channel);
                    }}
                  >
                    <span className={["channel-indicator-icon", `channel-base-color-${channel}`].join(" ")}></span>
                    {channel}
                  </div>
                );
              })}
              <div
                className="channel-name"
                onClick={() => {
                  changeChannel(CHANNELS.TOTAL);
                }}
              >
                <span className={["channel-indicator-icon", `channel-base-color-all`].join(" ")}></span>
                All (reset)
              </div>
            </div>
          </div>
        </div>
        {selectedChannel !== CHANNELS.TOTAL && (
          <p className="impact-radar-filtered-title">
            Impact Radar filtered on <span>{selectedChannel}</span> referrals
          </p>
        )}

        <div className="impact-radar-chart-container">
          <ImpactRadarGraph
            data={impactData.graphdata}
            effortData={transformedEngagementData}
            impactData={transformedImpactData}
            startDate={startDate}
            endDate={endDate}
            selectedChannel={selectedChannel}
            key={dataLoading ? "loading" : "loaded"}
            isLoading={dataLoading}
          />
        </div>
        <div className="impact-radar-legend">
          <span className={`color-${selectedChannel}-effort`}></span>Effort
          <span className={`color-${selectedChannel}-impact`} style={{ marginLeft: 20 }}></span>Impact
        </div>
      </div>
    </div>
  );
};

export default ImpactRadar;
